/**
 * Golf turn-by-turn: the announcement when you leave one hole for the next.
 *
 * A street nav app announces a maneuver because the driver has a decision to
 * make. Golf has exactly one recurring decision of that shape — you hole out,
 * and now you are driving to a tee you may never have seen. That is the moment
 * this covers, and it is the only moment: announcing anything mid-hole would be
 * noise, because the golfer is looking at the hole.
 *
 * Pure on purpose, like holeDetector: the policy is replayable from a recorded
 * round instead of needing a buggy and eighteen holes to test one edge case.
 *
 * 🟥 THE FLICKER PROBLEM. Fairways run side by side, so the detector can briefly
 * favour the neighbouring hole while you are still on this one. A banner that
 * appears every time that happens is worse than no banner — it trains the
 * golfer to ignore it, and it will happen most often on exactly the tight
 * parallel holes where a wrong yardage matters. Two guards: the detector must
 * be confident, and the same hole is never announced twice in a row.
 */

export interface HoleTurnState {
  /** Hole we last put on screen. null until the first hole is established. */
  announced: number | null;
}

export interface HoleTurn {
  hole: number;
  par: number;
  /** Hole length from the played tee, in the golfer's unit. */
  length: number;
  unit: 'm' | 'yd';
  /**
   * forward  — the normal 1 -> 2 progression
   * back     — 2 -> 1, a replayed hole or a wrong turn
   * jump     — anything else (shotgun start, skipping a queue)
   */
  direction: 'forward' | 'back' | 'jump';
}

export interface HoleTurnInput {
  hole: number;
  par: number;
  length: number;
  unit: 'm' | 'yd';
  /** 0-1 from the hole detector. */
  confidence: number;
}

export const initialHoleTurnState = (): HoleTurnState => ({ announced: null });

/**
 * Below this the detector is guessing between parallel holes and a banner would
 * flicker. Deliberately higher than the 0.35 the UI uses to show its "not sure
 * which hole" hint: showing a hedge is cheap, announcing a turn is not.
 */
export const MIN_CONFIDENCE = 0.55;

/**
 * Decide whether to announce a hole change.
 *
 * Returns the next state and the turn to show, or null for silence. Silence is
 * the common case and is not an error.
 */
export function decideHoleTurn(
  state: HoleTurnState,
  input: HoleTurnInput
): { state: HoleTurnState; turn: HoleTurn | null } {
  const { hole, par, length, unit, confidence } = input;

  if (!Number.isFinite(hole) || hole <= 0) return { state, turn: null };

  // First hole we ever settle on is an ARRIVAL, not a turn. Announcing it would
  // fire a "next hole" banner at the moment the app opens on the 1st tee, which
  // reads as a bug. Record it and stay quiet.
  if (state.announced === null) return { state: { announced: hole }, turn: null };

  if (hole === state.announced) return { state, turn: null };

  // The hole changed but the detector is not sure. Do NOT record it: if the
  // golfer really has moved on, confidence rises within a few fixes and we
  // announce then. Recording here would swallow the real turn.
  if (confidence < MIN_CONFIDENCE) return { state, turn: null };

  const delta = hole - state.announced;
  const direction: HoleTurn['direction'] = delta === 1 ? 'forward' : delta === -1 ? 'back' : 'jump';

  return {
    state: { announced: hole },
    turn: { hole, par, length, unit, direction },
  };
}

/** Banner copy. Kept next to the policy so wording and rules move together. */
export function holeTurnText(t: HoleTurn): { lead: string; detail: string } {
  const lead =
    t.direction === 'back' ? `Back to hole ${t.hole}` : `Next · Hole ${t.hole}`;
  const len = Math.round(t.length);
  const detail = len > 0 ? `Par ${t.par} · ${len}${t.unit}` : `Par ${t.par}`;
  return { lead, detail };
}
